/** Shared ring draft and cart orchestration. Loaded before Alpine when enabled. */
(() => {
  const config = window.CustomShopify.RingConfig;
  if (!config) return;
  const key = 'custom_ring_builder_v1';
  const message = config.messages;
  /** @type {CustomRingDraft} */
  let draft = read();
  let busy = false;
  /** @type {Map<string, Promise<number | null>>} */
  const mountingMaximumCarats = new Map();
  const widgetKey = 'custom_ring_widget_minimized';
  let minimized = readMinimized();
  /** @type {HTMLElement | null} */
  let opener = null;

  function readMinimized() {
    try { return localStorage.getItem(widgetKey) === 'true'; }
    catch { return false; }
  }
  /** @param {boolean} value */
  function setMinimized(value) {
    if (document.querySelector('.twc-ring-builder-section')) return;
    minimized = value;
    try { localStorage.setItem(widgetKey, String(value)); }
    catch { /* Keep the widget usable when preference storage is unavailable. */ }
  }

  /** @param {unknown} value @returns {value is CustomRingProduct} */
  function validProduct(value) {
    if (!value || typeof value !== 'object') return false;
    const p = /** @type {CustomRingProduct} */ (value);
    return /^\d+$/.test(p.productId) && /^\d+$/.test(p.variantId) &&
      typeof p.handle === 'string' && /^[\w-]+$/.test(p.handle) &&
      typeof p.title === 'string' && typeof p.price === 'number' && Number.isFinite(p.price) && p.price >= 0 &&
      typeof p.currency === 'string' && /^[A-Z]{3}$/.test(p.currency) &&
      (p.engraving === undefined || typeof p.engraving === 'string') &&
      (p.ringSize === undefined || /^(?:[7-9]|[12]\d|30)$/.test(p.ringSize)) &&
      (p.builderUrl === undefined || p.builderUrl === '' || (typeof p.builderUrl === 'string' && p.builderUrl.startsWith('/') && !p.builderUrl.startsWith('//')));
  }
  /** @returns {CustomRingDraft} */
  function read() {
    try {
      const value = JSON.parse(localStorage.getItem(key) || 'null');
      if (value?.version === 1 && value.collection === config?.collection &&
          Date.now() - value.updatedAt < 30 * 86400000 &&
          (!value.mounting || validProduct(value.mounting)) && (!value.diamond || validProduct(value.diamond)) &&
          (!value.submission || typeof value.submission === 'string')) {
        if (value.mounting && !value.mounting.ringSize && !value.submission) value.mounting = null;
        return value;
      }
    } catch { /* Storage may be unavailable; the current page remains usable. */ }
    return { version: 1, collection: config?.collection || '', updatedAt: Date.now(), mounting: null, diamond: null };
  }
  /** Persist before navigating; failure must not silently lose a configuration. @returns {boolean} */
  function save() {
    draft.updatedAt = Date.now();
    try {
      localStorage.setItem(key, JSON.stringify(draft));
      render();
      return true;
    } catch {
      render();
      status(message.storage || '');
      return false;
    }
  }
  /** @param {string} value */
  function status(value) {
    const node = document.querySelector('[data-ring-status]');
    if (node) node.textContent = value;
  }
  /** Show an action-specific validation result without relying on the review dialog being open. @param {HTMLButtonElement} button @param {string} value */
  function actionStatus(button, value) {
    const node = button.closest('[data-ring-action]')?.querySelector('[data-ring-action-status]');
    if (node instanceof HTMLElement) {
      node.textContent = value;
      node.hidden = !value;
    }
  }
  /** @param {number} limit */
  function caratLimitMessage(limit) {
    const maximum = new Intl.NumberFormat(document.documentElement.lang || 'it', { maximumFractionDigits: 3 }).format(limit);
    return (message.carat_limit_exceeded || message.no_band || '').replace('__MAXIMUM__', maximum);
  }
  /** @param {number} limit */
  function shortCaratLimitMessage(limit) {
    const maximum = new Intl.NumberFormat(document.documentElement.lang || 'it', { maximumFractionDigits: 3 }).format(limit);
    return (message.carat_limit_short || message.carat_limit_exceeded || '').replace('__MAXIMUM__', maximum);
  }
  /** @param {number} amount @param {string} currency */
  function money(amount, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency }).format(amount / 100);
  }
  /** @returns {{ amount: number, currency: string } | null} */
  function scalapayTotal() {
    if (!draft.mounting?.priceResolved || !draft.diamond || draft.mounting.currency !== draft.diamond.currency) return null;
    const amount = draft.mounting.price + draft.diamond.price;
    const minimum = Number(config.scalapay?.minimum);
    const maximum = Number(config.scalapay?.maximum);
    if (!Number.isFinite(amount) || !Number.isFinite(minimum) || !Number.isFinite(maximum)) return null;
    if (amount < minimum * 100 || amount > maximum * 100) return null;
    return { amount, currency: draft.mounting.currency };
  }
  /** @param {{ items?: Array<{ quantity: number, properties?: Record<string, string> }> }} current */
  function visibleCartItemCount(current) {
    let count = 0;
    const ringQuantities = new Map();
    for (const item of current.items || []) {
      const configuration = item.properties?._ring_configuration;
      if (!configuration) {
        count += Number(item.quantity) || 0;
        continue;
      }
      ringQuantities.set(configuration, Math.max(ringQuantities.get(configuration) || 0, Number(item.quantity) || 0));
    }
    for (const quantity of ringQuantities.values()) count += quantity;
    return count;
  }
  /** @param {string} path @param {RequestInit} [options] */
  async function request(path, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(path, { cache: 'no-store', signal: controller.signal, ...options });
      if (!response.ok) throw new Error(message.error);
      return response;
    } finally { clearTimeout(timer); }
  }
  /** @param {CustomRingProduct} product @param {'mounting'|'diamond'} role @param {CustomRingProduct} [diamond] @returns {Promise<CustomRingProduct>} */
  async function fresh(product, role, diamond) {
    const url = new URL(`${config?.root}products/${encodeURIComponent(product.handle)}`, location.origin);
    url.searchParams.set('variant', product.variantId);
    url.searchParams.set('section_id', 'custom_ring_product_data');
    const html = await (await request(url.href)).text();
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ring-product-data]');
    let current = JSON.parse(node?.textContent || 'null');
    if (!validProduct(current) || current.productId !== product.productId ||
        (role === 'diamond' ? !current.eligible : !current.mounting)) throw new Error(message.unavailable);
    if (role === 'mounting') {
      if (!product.ringSize || !/^(?:[7-9]|[12]\d|30)$/.test(product.ringSize) || !product.material || !product.engravingOption) throw new Error(message.unavailable);
      const carats = Number(diamond?.carats);
      if (!(carats > 0) || !Number.isFinite(carats)) throw new Error(message.carats_missing);
      const bands = [...(current.bands || [])];
      const visited = new Set();
      while (current.nextPage) {
        const page = new URL(current.nextPage, url);
        page.searchParams.set('section_id', 'custom_ring_product_data');
        page.searchParams.set('variant', product.variantId);
        if (page.origin !== url.origin || visited.has(page.href)) throw new Error(message.error);
        visited.add(page.href);
        const pageHtml = await (await request(page.href)).text();
        const pageNode = new DOMParser().parseFromString(pageHtml, 'text/html').querySelector('[data-ring-product-data]');
        const next = JSON.parse(pageNode?.textContent || 'null');
        if (!validProduct(next) || next.productId !== product.productId) throw new Error(message.error);
        bands.push(...(next.bands || []));
        current.nextPage = next.nextPage;
      }
      const matchingBands = bands.filter(item => item.material === product.material && item.engravingOption === product.engravingOption &&
        Number.isFinite(Number(item.diamondCaratLimit)));
      const maximumCarats = matchingBands.reduce((maximum, item) => Math.max(maximum, Number(item.diamondCaratLimit)), Number.NEGATIVE_INFINITY);
      const band = matchingBands.filter(item => Number(item.diamondCaratLimit) >= carats)
        .sort((a, b) => Number(a.diamondCaratLimit) - Number(b.diamondCaratLimit))[0];
      if (!band) throw new Error(Number.isFinite(maximumCarats) ? caratLimitMessage(maximumCarats) : message.no_band);
      current = { ...current, ...band, priceResolved: true };
      delete current.bands;
      delete current.nextPage;
    } else if (current.variantId !== product.variantId) throw new Error(message.unavailable);
    if (!current.available || current.minimum !== 1 || current.increment !== 1) throw new Error(message.unavailable);
    return { ...current, engraving: product.engraving || '', ringSize: product.ringSize, builderUrl: product.builderUrl || '' };
  }
  /** Fetch one saved mounting once and reuse its material/engraving carat ceiling for PLP actions. @param {CustomRingProduct} mounting @returns {Promise<number | null>} */
  function maximumMountingCarats(mounting) {
    const cacheKey = [mounting.productId, mounting.variantId, mounting.material, mounting.engravingOption].join(':');
    const cached = mountingMaximumCarats.get(cacheKey);
    if (cached) return cached;
    const result = (async () => {
      const url = new URL(`${config?.root}products/${encodeURIComponent(mounting.handle)}`, location.origin);
      url.searchParams.set('variant', mounting.variantId);
      url.searchParams.set('section_id', 'custom_ring_product_data');
      const html = await (await request(url.href)).text();
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ring-product-data]');
      const current = JSON.parse(node?.textContent || 'null');
      if (!validProduct(current) || current.productId !== mounting.productId || !current.mounting) return null;
      const bands = [...(current.bands || [])];
      const visited = new Set();
      let nextPage = current.nextPage;
      while (nextPage) {
        const page = new URL(nextPage, url);
        page.searchParams.set('section_id', 'custom_ring_product_data');
        page.searchParams.set('variant', mounting.variantId);
        if (page.origin !== url.origin || visited.has(page.href)) return null;
        visited.add(page.href);
        const pageHtml = await (await request(page.href)).text();
        const pageNode = new DOMParser().parseFromString(pageHtml, 'text/html').querySelector('[data-ring-product-data]');
        const next = JSON.parse(pageNode?.textContent || 'null');
        if (!validProduct(next) || next.productId !== mounting.productId) return null;
        bands.push(...(next.bands || []));
        nextPage = next.nextPage;
      }
      const limits = bands.filter(band => band.material === mounting.material && band.engravingOption === mounting.engravingOption)
        .map(band => Number(band.diamondCaratLimit)).filter(Number.isFinite);
      return limits.length ? Math.max(...limits) : null;
    })().catch(() => null);
    mountingMaximumCarats.set(cacheKey, result);
    return result;
  }
  /** Update PLP/PDP diamond actions once the saved mounting's ceiling is known. */
  function updateDiamondCaratSafeguards() {
    const mounting = draft.mounting;
    const actions = [...document.querySelectorAll('[data-ring-select]')].filter((button) => button instanceof HTMLButtonElement);
    if (!mounting) {
      for (const button of actions) {
        if (button.dataset.ringCaratLimit === 'true') {
          button.disabled = button.dataset.ringInitiallyDisabled === 'true';
          delete button.dataset.ringCaratLimit;
          actionStatus(button, '');
        }
      }
      return;
    }
    void maximumMountingCarats(mounting).then((maximum) => {
      if (!maximum || draft.mounting !== mounting) return;
      for (const button of actions) {
        const productData = button.closest('[data-ring-action]')?.querySelector('[data-ring-product-data]');
        let carats = Number.NaN;
        try { carats = Number(JSON.parse(productData?.textContent || 'null')?.carats); }
        catch { continue; }
        if (!Number.isFinite(carats) || carats <= 0) continue;
        if (button.dataset.ringInitiallyDisabled === undefined) button.dataset.ringInitiallyDisabled = String(button.disabled);
        if (carats > maximum) {
          button.disabled = true;
          button.dataset.ringCaratLimit = 'true';
          actionStatus(button, shortCaratLimitMessage(maximum));
        } else if (button.dataset.ringCaratLimit === 'true') {
          button.disabled = button.dataset.ringInitiallyDisabled === 'true';
          delete button.dataset.ringCaratLimit;
          actionStatus(button, '');
        }
      }
    });
  }
  function render() {
    const widget = document.querySelector('[data-ring-widget]');
    if (widget instanceof HTMLElement) widget.hidden = !draft.mounting && !draft.diamond;
    const dock = document.querySelector('.twc-selection-dock');
    const compare = document.querySelector('.twc-compare-launcher-shell');
    const combined = Boolean((draft.mounting || draft.diamond) && compare instanceof HTMLElement && !compare.hidden);
    const compact = minimized || combined || Boolean(document.querySelector('.twc-ring-builder-section'));
    if (widget instanceof HTMLElement) widget.classList.toggle('twc-ring-widget--compact', compact);
    if (dock instanceof HTMLElement) dock.classList.toggle('twc-selection-dock--combined', combined);
    const launcherLabel = document.querySelector('[data-ring-launcher-label]');
    if (launcherLabel) launcherLabel.textContent = (compact ? message.your_ring : message.resume) || '';
    const details = document.querySelector('[data-ring-widget-details]');
    if (details instanceof HTMLElement) details.hidden = compact;
    const product = draft.mounting || draft.diamond;
    const dockImage = document.querySelector('[data-ring-dock-image]');
    if (dockImage instanceof HTMLImageElement) {
      dockImage.hidden = !compact || !product?.image;
      if (product?.image) dockImage.src = product.image;
    }
    const summary = document.querySelector('[data-ring-summary]');
    if (summary) summary.textContent = product ? [product.title,
      product.mounting && !product.priceResolved ? product.material : product.variantTitle,
      product.ringSize ? `${message.ring_size}: ${product.ringSize}` : ''].filter(Boolean).join(' · ') : '';
    const image = document.querySelector('[data-ring-image]');
    if (image instanceof HTMLImageElement) {
      image.hidden = !product?.image;
      if (product?.image) image.src = product.image;
    }
    for (const button of document.querySelectorAll('[data-ring-select]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const cardAction = button.closest('[data-ring-requires-mounting]');
      button.hidden = Boolean(cardAction) && !draft.mounting;
      if (cardAction instanceof HTMLElement) cardAction.hidden = button.hidden;
      if (!button.disabled) button.textContent = draft.mounting ? message.select || '' : message.start_diamond || '';
    }
    const review = document.querySelector('[data-ring-review]');
    if (review) {
      // Keep Liquid-owned destinations and translated labels across row rebuilds.
      const editLinks = {
        mounting: review.querySelector('[data-ring-edit]'),
        diamond: review.querySelector('[data-ring-choose-diamond]'),
      };
      review.replaceChildren();
      for (const role of /** @type {const} */ (['mounting', 'diamond'])) {
        const item = draft[role];
        const row = document.createElement('div');
        row.className = 'twc-ring-review-row';
        const media = document.createElement('div');
        media.className = 'twc-ring-review-media';
        media.setAttribute('aria-hidden', 'true');
        if (item?.image) {
          const thumbnail = document.createElement('img');
          thumbnail.src = item.image;
          thumbnail.alt = '';
          thumbnail.width = 80;
          thumbnail.height = 80;
          thumbnail.addEventListener('error', () => { thumbnail.remove(); media.textContent = '\u25c7'; }, { once: true });
          media.append(thumbnail);
        } else media.textContent = '\u25c7';
        const copy = document.createElement('div');
        copy.className = 'twc-ring-review-copy';
        const heading = document.createElement('p');
        heading.className = 'twc-ring-review-label';
        heading.textContent = message[role] || '';
        const text = document.createElement('p');
        text.className = 'twc-ring-review-title';
        text.textContent = item ? item.title : message.missing || '';
        copy.append(heading, text);
        if (item?.variantTitle && item.variantTitle !== 'Default Title' && item.variantTitle !== item.title) {
          const variant = document.createElement('p');
          variant.textContent = role === 'mounting' && !item.priceResolved ? `${item.material} / ${item.engravingOption}` : item.variantTitle;
          copy.append(variant);
        }
        if (item?.engraving) {
          const engraving = document.createElement('p');
          engraving.textContent = `${message.engraving}: ${item.engraving}`;
          copy.append(engraving);
        }
        if (item?.ringSize) {
          const size = document.createElement('p');
          size.textContent = `${message.ring_size}: ${item.ringSize}`;
          copy.append(size);
        }
        const actions = document.createElement('div');
        actions.className = 'twc-ring-review-actions';
        const editLink = editLinks[role];
        if (editLink instanceof HTMLElement) {
          if (role === 'diamond') {
            const label = item ? editLink.dataset.ringChangeLabel : editLink.dataset.ringChooseLabel;
            if (label) editLink.textContent = label;
          }
          actions.append(editLink);
        }
        if (item) {
          const price = document.createElement('p');
          price.className = 'twc-ring-review-price';
          price.textContent = money(item.price, item.currency);
          if (role !== 'mounting' || item.priceResolved) copy.append(price);
          if (role === 'mounting' && !item.priceResolved) {
            const note = document.createElement('p');
            note.textContent = message.price_pending || '';
            copy.append(note);
          }
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'button-unstyled twc-ring-review-remove';
          remove.dataset.ringRemovePart = role;
          remove.textContent = message[`remove_${role}`] || '';
          remove.disabled = busy || Boolean(draft.submission);
          actions.append(remove);
        }
        copy.append(actions);
        row.append(media, copy);
        review.append(row);
      }
    }
    const total = document.querySelector('[data-ring-total]');
    if (total) {
      total.replaceChildren();
      if (draft.mounting?.priceResolved && draft.diamond && draft.mounting.currency === draft.diamond.currency) {
        const label = document.createElement('span');
        label.textContent = message.total || '';
        const amount = document.createElement('strong');
        amount.textContent = money(draft.mounting.price + draft.diamond.price, draft.mounting.currency);
        total.append(label, amount);
      }
    }
    const scalapay = document.querySelector('[data-ring-scalapay]');
    if (scalapay instanceof HTMLElement) {
      scalapay.replaceChildren();
      scalapay.hidden = true;
      const payment = scalapayTotal();
      if (payment) {
        const prefix = document.createElement('span');
        prefix.textContent = message.scalapay_prefix || '';
        const installment = document.createElement('strong');
        installment.className = 'tw:underline';
        installment.textContent = `${money(Math.round(payment.amount / 4), payment.currency)}${message.scalapay_rate_suffix || ''}`;
        const suffix = document.createElement('span');
        suffix.textContent = message.scalapay_suffix || '';
        scalapay.append(prefix, installment, suffix);
        if (config.scalapay?.icon) {
          const icon = document.createElement('img');
          icon.src = config.scalapay.icon;
          icon.alt = '';
          icon.width = 64;
          icon.height = 15;
          icon.style.cssText = 'width: 64px; height: auto; max-width: 64px; flex: 0 0 64px;';
          icon.setAttribute('aria-hidden', 'true');
          scalapay.append(icon);
        }
        scalapay.hidden = false;
      }
    }
    const add = document.querySelector('[data-ring-add]');
    if (add instanceof HTMLButtonElement) add.disabled = busy || !draft.mounting || !draft.diamond || (!draft.mounting.priceResolved && !draft.submission);
    const edit = document.querySelector('[data-ring-edit]');
    if (edit instanceof HTMLAnchorElement) edit.href = draft.mounting?.builderUrl || config?.builderUrl || '#';
    for (const button of document.querySelectorAll('[data-ring-select], [data-ring-remove]')) {
      if (button instanceof HTMLButtonElement) {
        if (busy) {
          if (button.dataset.ringWasDisabled === undefined) button.dataset.ringWasDisabled = String(button.disabled);
          button.disabled = true;
        } else if (button.dataset.ringWasDisabled !== undefined) {
          button.disabled = button.dataset.ringWasDisabled === 'true';
          delete button.dataset.ringWasDisabled;
        }
      }
    }
    updateDiamondCaratSafeguards();
    window.dispatchEvent(new CustomEvent('custom:ring:change'));
  }
  function open() {
    const dialog = document.querySelector('[data-ring-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    render();
    if (!dialog.open) {
      opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      window.dispatchEvent(new CustomEvent('custom:panel:open', { detail: 'ring' }));
      dialog.showModal();
    }
  }
  /** Remove a draft component only; submitted cart pairs keep their recovery identity.
   * @param {string | undefined} role
   * @param {boolean} [restoreFocus]
   * @returns {boolean}
   */
  function removePart(role, restoreFocus = true) {
    if (busy || (role !== 'mounting' && role !== 'diamond')) return false;
    if (draft.submission) { status(message.pending || ''); return false; }
    if (!draft[role]) return true;
    const previous = structuredClone(draft);
    draft[role] = null;
    if (role === 'diamond' && draft.mounting) draft.mounting.priceResolved = false;
    if (!save()) {
      draft = previous;
      render();
      return false;
    }
    status(message[`${role}_removed`] || '');
    const next = document.querySelector(role === 'mounting' ? '[data-ring-edit]' : '[data-ring-choose-diamond]');
    if (restoreFocus && next instanceof HTMLElement) next.focus();
    return true;
  }
  /** @param {CustomRingProduct} product @param {boolean} [advance] @returns {Promise<boolean>} */
  async function confirmMounting(product, advance = true) {
    if (busy || !validProduct(product) || !product.ringSize || !product.material || !product.engravingOption) return false;
    if (draft.submission) { open(); status(message.pending || ''); return false; }
    busy = true;
    product = { ...product, priceResolved: false };
    render();
    status('');
    try {
      if (draft.diamond) {
        const diamond = await fresh(draft.diamond, 'diamond');
        product = await fresh(product, 'mounting', diamond);
        draft.diamond = diamond;
      }
    } catch (error) {
      open(); status(error instanceof Error ? error.message : message.error || '');
      return false;
    } finally { busy = false; render(); }
    draft.mounting = product;
    delete draft.submission;
    const persisted = save();
    if (!persisted) { open(); return false; }
    if (!advance) return true;
    if (draft.diamond) open();
    else {
      const section = document.querySelector('[data-ring-diamonds]');
      if (section instanceof HTMLElement) { section.scrollIntoView({ block: 'start' }); section.focus({ preventScroll: true }); }
      else open();
    }
    return true;
  }
  /** @param {HTMLButtonElement} button */
  async function selectDiamond(button) {
    if (busy) return;
    if (button.closest('[data-ring-requires-mounting]') && !draft.mounting) return;
    if (draft.submission) { open(); status(message.pending || ''); return; }
    actionStatus(button, '');
    busy = true;
    render();
    try {
      const node = button.closest('[data-ring-action]')?.querySelector('[data-ring-product-data]');
      const product = JSON.parse(node?.textContent || 'null');
      if (!validProduct(product)) throw new Error(message.error);
      if (/^(?:[7-9]|[12]\d|30)$/.test(button.dataset.ringSize || '')) product.ringSize = button.dataset.ringSize;
      const form = button.closest('product-form-component')?.querySelector('input[name="id"]');
      if (form instanceof HTMLInputElement) product.variantId = form.value;
      else {
        const card = button.closest('product-card');
        const selected = card?.querySelector('input:checked[data-variant-id]');
        if (selected instanceof HTMLInputElement && selected.dataset.variantId) product.variantId = selected.dataset.variantId;
        else {
          const link = card?.querySelector('a[ref="productCardLink"]');
          if (link instanceof HTMLAnchorElement) product.variantId = new URL(link.href).searchParams.get('variant') || product.variantId;
        }
      }
      const diamond = await fresh(product, 'diamond');
      if (!(Number(diamond.carats) > 0)) throw new Error(message.carats_missing);
      const mounting = draft.mounting ? await fresh(draft.mounting, 'mounting', diamond) : null;
      draft.diamond = diamond;
      draft.mounting = mounting;
      delete draft.submission;
      if (!save()) { open(); return; }
      if (!draft.mounting) location.assign(button.dataset.ringBuilderUrl || config?.builderUrl || '#');
      else { status(''); open(); }
    } catch (error) {
      const value = error instanceof Error ? error.message : message.error || '';
      actionStatus(button, value);
      open();
      status(value);
    }
    finally { busy = false; render(); }
  }
  /**
   * Saves a PDP diamond together with the mounting currently previewed on that
   * PDP. The full builder restores this draft without crawler-facing URL params.
   * @param {HTMLButtonElement} button
   * @param {'add'|'builder'} intent
   */
  async function selectPdpPreview(button, intent) {
    if (busy || button.disabled) return;
    let mounting;
    try { mounting = JSON.parse(button.dataset.ringPreviewMounting || 'null'); }
    catch { return; }
    if (!validProduct(mounting) || !mounting.mounting) return;
    const node = button.closest('[data-ring-action]')?.querySelector('[data-ring-product-data]');
    let source;
    try { source = JSON.parse(node?.textContent || 'null'); }
    catch { return; }
    if (!validProduct(source)) return;
    actionStatus(button, '');
    busy = true;
    render();
    try {
      const diamond = await fresh(source, 'diamond');
      mounting = await fresh(mounting, 'mounting', diamond);
      draft.diamond = diamond;
      draft.mounting = mounting;
      delete draft.submission;
      if (!save()) return;
      if (intent === 'builder') {
        location.assign(mounting.builderUrl || config?.builderUrl || '#');
        return;
      }
      busy = false;
      render();
      await add();
    } catch (error) {
      const value = error instanceof Error ? error.message : message.error || '';
      actionStatus(button, value);
      status(value);
      open();
    } finally {
      busy = false;
      render();
    }
  }
  /** @returns {Promise<{item_count: number, items: Array<{key: string, variant_id: number, quantity: number, properties: Record<string, string>}>}>} */
  async function cart() { return (await request(`${config?.root}cart.js`)).json(); }
  /** @param {Record<string, number>} updates */
  async function updateCart(updates) {
    const result = await (await request(`${config?.root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) })).json();
    if (result.errors || result.status >= 400) throw new Error(message.error);
  }
  async function add() {
    if (busy || !draft.mounting || !draft.diamond) return;
    busy = true;
    render();
    status(message.checking || '');
    try {
      if (draft.submission) {
        const previous = (await cart()).items.filter(item => item.properties?._ring_configuration === draft.submission);
        if (previous.length === 2 && previous.every(item => item.quantity === 1) &&
            previous.some(item => String(item.variant_id) === draft.mounting?.variantId && item.properties._ring_role === 'mounting') &&
            previous.some(item => String(item.variant_id) === draft.diamond?.variantId && item.properties._ring_role === 'diamond')) {
          await finish();
          return;
        }
        if (previous.length) await updateCart(Object.fromEntries(previous.map(item => [item.key, 0])));
        delete draft.submission;
        save();
      }
      const diamond = await fresh(draft.diamond, 'diamond');
      const mounting = await fresh(draft.mounting, 'mounting', diamond);
      const changed = mounting.price !== draft.mounting.price || diamond.price !== draft.diamond.price ||
        mounting.variantId !== draft.mounting.variantId || !draft.mounting.priceResolved ||
        mounting.currency !== draft.mounting.currency || diamond.currency !== draft.diamond.currency;
      draft.mounting = mounting;
      draft.diamond = diamond;
      if (!save()) return;
      if (changed) { status(message.price_changed || ''); return; }
      if (mounting.currency !== diamond.currency) throw new Error(message.error);
      draft.submission ||= crypto.randomUUID();
      if (!save()) return;
      // Reconcile a previous ambiguous response before retrying, never duplicate the pair.
      let current = await cart();
      let paired = current.items.filter(item => item.properties?._ring_configuration === draft.submission);
      const complete = () => paired.length === 2 && paired.every(item => item.quantity === 1) &&
        paired.some(item => String(item.variant_id) === mounting.variantId && item.properties._ring_role === 'mounting') &&
        paired.some(item => String(item.variant_id) === diamond.variantId && item.properties._ring_role === 'diamond');
      if (!complete()) {
        if (paired.length) await updateCart(Object.fromEntries(paired.map(item => [item.key, 0])));
        const common = { _ring_configuration: draft.submission };
        const items = [
          { id: mounting.variantId, quantity: 1, properties: { ...common, _ring_role: 'mounting', Misura: mounting.ringSize, Diamante: diamond.sku || diamond.variantId, ...(mounting.engraving ? { Incisione: mounting.engraving } : {}) } },
          { id: diamond.variantId, quantity: 1, properties: { ...common, _ring_role: 'diamond', Montatura: mounting.sku || mounting.variantId } },
        ];
        try {
          await request(`${config?.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
        } catch { /* Verify the actual cart even if the response was lost. */ }
        current = await cart();
        paired = current.items.filter(item => item.properties?._ring_configuration === draft.submission);
        if (!complete()) {
          if (paired.length) await updateCart(Object.fromEntries(paired.map(item => [item.key, 0])));
          delete draft.submission;
          save();
          throw new Error(message.error);
        }
      }
      window.CustomShopify.Analytics?.pushEvent({ event: 'cart:add_ring_builder', configuration_id: draft.submission, mounting_variant_id: mounting.variantId, diamond_variant_id: diamond.variantId });
      await finish();
    } catch (error) {
      status(error instanceof Error ? error.message : message.error || '');
      const link = document.querySelector('[data-ring-cart]');
      if (link instanceof HTMLElement) link.hidden = false;
    } finally { busy = false; render(); }
  }
  async function refreshCart() {
    const [{ CartUpdateEvent }, current] = await Promise.all([import('@theme/events'), cart()]);
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer) await customElements.whenDefined('cart-drawer-component');
    const dialog = document.querySelector('[data-ring-dialog]');
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      await new Promise(resolve => {
        dialog.addEventListener('close', resolve, { once: true });
        dialog.close();
      });
    }
    // Native listeners refresh cart sections and update the header count.
    document.dispatchEvent(new CartUpdateEvent(current, 'custom-ring-builder', {
      source: 'custom-ring-builder', itemCount: visibleCartItemCount(current),
    }));
    if (drawer && !drawer.hasAttribute('auto-open') && 'open' in drawer && typeof drawer.open === 'function') drawer.open();
    else if (!drawer) location.assign(`${config?.root}cart`);
  }
  async function finish() {
    await refreshCart();
    draft = { version: 1, collection: config?.collection || '', updatedAt: Date.now(), mounting: null, diamond: null };
    save();
  }
  /** @param {HTMLButtonElement} button */
  async function remove(button) {
    if (busy) return;
    busy = true;
    render();
    try {
      const current = await cart();
      const paired = current.items.filter(item => item.properties?._ring_configuration === button.dataset.ringRemove);
      if (paired.length) await updateCart(Object.fromEntries(paired.map(item => [item.key, 0])));
      await refreshCart();
    } catch {
      const error = button.closest('[data-ring-cart-card]')?.querySelector('[data-ring-cart-error]');
      if (error) error.textContent = message.error || '';
      else { open(); status(message.error || ''); }
    }
    finally { busy = false; render(); }
  }
  /**
   * Select a curated diamond with the builder's current, unsaved mounting.
   * Stay on the builder; the customer's next action opens the review.
   * @param {CustomRingProduct} source
   * @param {CustomRingProduct} mounting
   * @returns {Promise<CustomRingProduct | null>}
   */
  async function selectPair(source, mounting) {
    if (busy) return null;
    if (draft.submission) throw new Error(message.pending);
    if (!validProduct(source) || !validProduct(mounting) || !mounting.mounting) throw new Error(message.unavailable);
    busy = true;
    render();
    try {
      const diamond = await fresh(source, 'diamond');
      const currentMounting = await fresh(mounting, 'mounting', diamond);
      if (diamond.currency !== currentMounting.currency) throw new Error(message.error);
      const previous = structuredClone(draft);
      draft.diamond = diamond;
      draft.mounting = currentMounting;
      delete draft.submission;
      if (!save()) {
        draft = previous;
        render();
        throw new Error(message.storage);
      }
      return structuredClone(currentMounting);
    } finally {
      busy = false;
      render();
    }
  }
  window.CustomShopify.RingBuilder = { get: () => structuredClone(draft), confirmMounting, selectPair, clearDiamond: () => removePart('diamond', false), open };
  document.addEventListener('click', event => {
    if (event.target instanceof Element && event.target.closest('[data-ring-cart]') && document.querySelector('cart-drawer-component')) {
      event.preventDefault();
      void refreshCart().catch(() => status(message.error || ''));
      return;
    }
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.hasAttribute('data-ring-remove-part')) { event.preventDefault(); removePart(button.dataset.ringRemovePart); }
    if (button.hasAttribute('data-ring-select')) { event.preventDefault(); event.stopPropagation(); void selectDiamond(button); }
    if (button.hasAttribute('data-ring-preview-add')) { event.preventDefault(); event.stopPropagation(); void selectPdpPreview(button, 'add'); }
    if (button.hasAttribute('data-ring-preview-builder')) { event.preventDefault(); event.stopPropagation(); void selectPdpPreview(button, 'builder'); }
    if (button.hasAttribute('data-ring-open')) { setMinimized(false); open(); }
    if (button.hasAttribute('data-ring-minimize')) { setMinimized(true); render(); }
    if (button.hasAttribute('data-ring-close')) document.querySelector('dialog[data-ring-dialog]')?.dispatchEvent(new CustomEvent('ring-close'));
    if (button.hasAttribute('data-ring-add')) void add();
    if (button.hasAttribute('data-ring-remove')) { event.preventDefault(); void remove(button); }
  });
  window.addEventListener('storage', event => {
    if (event.key === widgetKey || event.key === null) { minimized = readMinimized(); render(); }
    if (event.key === key && !busy) { draft = read(); render(); }
  });
  function init() {
    const dialog = document.querySelector('[data-ring-dialog]');
    if (dialog instanceof HTMLDialogElement) {
      dialog.addEventListener('ring-close', () => dialog.close());
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => opener?.isConnected && opener.focus());
    }
    render();
    hydrateCompareActions();
    // Newly rendered collection cards and quick-add responses share the same delegated actions.
    new MutationObserver(records => {
      if (records.some(record => Array.from(record.addedNodes).some(node => node instanceof Element &&
          (node.matches('[data-ring-action]') || node.querySelector('[data-ring-action]'))))) render();
      if (records.some(record => Array.from(record.addedNodes).some(node => node instanceof Element &&
          (node.matches('[data-ring-compare-url]') || node.querySelector('[data-ring-compare-url]'))))) hydrateCompareActions();
    }).observe(document.body, { childList: true, subtree: true });
  }
  /** Populate comparison actions from current Liquid eligibility, including variant changes. */
  function hydrateCompareActions() {
    for (const slot of document.querySelectorAll('[data-ring-compare-url]')) {
      if (slot instanceof HTMLElement) void hydrateCompareAction(slot);
    }
  }
  /** @param {HTMLElement} slot */
  async function hydrateCompareAction(slot) {
    const select = slot.closest('.twc-compare-product-actions')?.querySelector('[data-compare-variant-select]');
    const variantId = select instanceof HTMLSelectElement ? select.value : slot.dataset.ringCompareVariant || '';
    if (!variantId || slot.dataset.ringLoadedVariant === variantId) return;
    slot.dataset.ringLoadedVariant = variantId;
    slot.replaceChildren();
    try {
      const url = new URL(slot.dataset.ringCompareUrl || '', location.origin);
      // Keep Section Rendering on the active storefront/preview origin.
      url.host = location.host;
      url.protocol = location.protocol;
      url.searchParams.delete('view');
      url.searchParams.set('section_id', 'custom_ring_product_data');
      url.searchParams.set('variant', variantId);
      const html = await (await request(url.href)).text();
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ring-product-data]');
      const product = JSON.parse(node?.textContent || 'null');
      if (!slot.isConnected || slot.dataset.ringLoadedVariant !== variantId) return;
      if (!validProduct(product) || !product.eligible || product.productId !== slot.dataset.ringCompareProduct || product.variantId !== variantId) return;
      const action = document.createElement('div');
      action.setAttribute('data-ring-action', '');
      const data = document.createElement('script');
      data.type = 'application/json';
      data.setAttribute('data-ring-product-data', '');
      data.textContent = JSON.stringify(product);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button-secondary twc-compare-product-actions__button';
      button.setAttribute('data-ring-select', '');
      button.disabled = !product.available;
      button.textContent = product.available ? (draft.mounting ? message.select : message.start_diamond) || '' : message.unavailable || '';
      action.append(data, button);
      slot.append(action);
      render();
    } catch {
      if (slot.dataset.ringLoadedVariant === variantId) delete slot.dataset.ringLoadedVariant;
    }
  }
  document.addEventListener('change', event => {
    if (event.target instanceof Element && event.target.matches('[data-compare-variant-select]')) hydrateCompareActions();
  });
  window.addEventListener('custom:compare:change', render);
  window.addEventListener('custom:panel:open', event => {
    if (event instanceof CustomEvent && event.detail === 'compare') {
      const dialog = document.querySelector('[data-ring-dialog]');
      if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
